"use client";

import { useCallback } from "react";
import { Button, Link, Text } from "@zapier/design-system";
import { useBulkTrashAssets, useCurrentUser } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import {
  DEFAULT_FOLDER_ID,
  getFolderUrl,
  getVirtualFolderName,
  useNotification,
} from "@zapier/utils";

import styles from "./useRestoreNotification.module.css";

export interface RestoreDestination {
  id?: string | null;
  name?: string;
}

export interface SingleRestore {
  assetName?: string;
  folder?: RestoreDestination;
  /** Asset owner id, used to tell whose personal folder a folderless restore lands in. */
  owner?: { id?: number };
}

interface NotifyInput {
  succeededAssetIds: string[];
  /** Present when exactly one asset is restored and its name/folder are known. */
  single?: SingleRestore;
}

interface Input {
  folderId?: string;
}

/**
 * Builds the destination-aware success toast shown after a trash restore, and
 * wires its Undo button to re-trash the restored ids.
 */
export function useRestoreNotification({ folderId }: Input) {
  const t = useTranslations("buttons");
  const tFallbacks = useTranslations("fallbacks");
  const { createNotification } = useNotification();
  const { user } = useCurrentUser();
  const bulkTrashAssets = useBulkTrashAssets({ folderId });

  const undoRestore = useCallback(
    async (assetIds: string[], assetName?: string) => {
      try {
        // A resolved mutation is not a re-trashed asset: bulkTrash reports
        // per-id outcomes in its arrays rather than throwing on failure.
        const { succeededAssetIds, failedAssetIds } =
          await bulkTrashAssets.mutateAsync({ assetIds });

        if (failedAssetIds.length === 0) {
          createNotification({
            message:
              assetName && succeededAssetIds.length === 1
                ? t("restore-undo-success-single", { assetName })
                : t("restore-undo-success", {
                    count: succeededAssetIds.length,
                  }),
            type: "success",
          });
        } else if (succeededAssetIds.length === 0) {
          createNotification({
            message: t("restore-undo-error"),
            type: "error",
          });
        } else {
          createNotification({
            message: t("restore-undo-partial", {
              succeededCount: succeededAssetIds.length,
              failedCount: failedAssetIds.length,
            }),
            type: "warning",
          });
        }
      } catch {
        createNotification({ message: t("restore-undo-error"), type: "error" });
      }
    },
    [bulkTrashAssets, createNotification, t],
  );

  const notifyRestoreSuccess = useCallback(
    ({ succeededAssetIds, single }: NotifyInput) => {
      const onUndo = () =>
        void undoRestore(succeededAssetIds, single?.assetName);

      if (single) {
        const assetName =
          single.assetName || tFallbacks("untitled-unknown-asset");

        const ownerId = single.owner?.id;
        const isOtherOwner = ownerId != null && ownerId !== user?.id;

        // Resolve the destination and a View link only when we can navigate to it.
        let destination: string;
        let destinationHref: string | undefined;
        if (single.folder?.name) {
          destination = single.folder.name;
          // A named folder with no id can't be navigated to (e.g. it was
          // deleted after the asset was trashed), so name it without a link.
          destinationHref = single.folder.id
            ? getFolderUrl(single.folder.id)
            : undefined;
        } else if (isOtherOwner) {
          // Folderless restore of someone else's asset (e.g. an admin restoring
          // a member's asset) lands in that member's personal folder. Whether an
          // admin can view another member's personal folder is unresolved, so
          // name it generically without a link.
          destination = t("restore-owner-personal-folder");
          destinationHref = undefined;
        } else {
          destination = getVirtualFolderName(DEFAULT_FOLDER_ID, { user });
          destinationHref = getFolderUrl(null);
        }

        return createNotification({
          message: (
            <div className={styles.restoreToast}>
              <Text>
                {t("restore-success-single", { assetName, destination })}
              </Text>
              <div className={styles.actions}>
                <Button variant="secondary" size="small" onPress={onUndo}>
                  {t("restore-undo")}
                </Button>
                {destinationHref && (
                  <Link href={destinationHref} variant="primary" size="small">
                    {t("restore-view", { destination })}
                  </Link>
                )}
              </div>
            </div>
          ),
          type: "success",
        });
      }

      createNotification({
        message: (
          <div className={styles.restoreToast}>
            <Text>
              {t("restore-success-bulk", { count: succeededAssetIds.length })}
            </Text>
            <div className={styles.actions}>
              <Button variant="secondary" size="small" onPress={onUndo}>
                {t("restore-undo")}
              </Button>
            </div>
          </div>
        ),
        type: "success",
      });
    },
    [createNotification, t, tFallbacks, undoRestore, user],
  );

  return { notifyRestoreSuccess };
}
